import { SystemFileAPI } from '@business/api/system_file';
import { SystemFileId, SystemFileResponse } from '@business/entities/system_files/SystemFileResponse';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap } from 'rxjs/operators';
import { map } from 'rxjs/operators';

export class SystemFileBloc extends BaseBloc {
  private readonly itemRepo = new Repository<SystemFileResponse>({
    getItemId: term => term.id,
  });

  readonly items$ = this.itemRepo.items$.pipe(map(items => R.sortBy(items, 'menuOrder')));
  readonly selectTemplate = this.itemRepo.selectItem;

  onReset() {
    this.itemRepo.reset();
  }

  fetchSystemFiles = (status: number = 1) => {
    return SystemFileAPI.fetchSystemFiles(status).pipe(
      this.itemRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_system_file_error') }),
    );
  };

  fetchSystemFile = (id: SystemFileId) => {
    return SystemFileAPI.fetchSystemFile(id).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_system_file_error') }),
    );
  };

  createSystemFile = (type: string) => {
    return SystemFileAPI.createSystemFile(type).pipe(
      this.itemRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_system_file'),
      }),
    );
  };

  updateSystemFile = (id: SystemFileId, data: Partial<SystemFileResponse>) => {
    return SystemFileAPI.updateSystemFile(id, data).pipe(
      this.itemRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_system_file'),
      }),
    );
  };

  deleteSystemFile = (id: SystemFileId) => {
    return showDeleteConfirmation('Delete Term', `Do you really want to remove this file?`).pipe(
      switchMap(() => SystemFileAPI.deleteSystemFile(id)),
      this.itemRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_system_file'),
      }),
    );
  };

  restoreSystemFile = (id: SystemFileId) => {
    return showDeleteConfirmation('Restore Term', `Do you really want to restore this file?`).pipe(
      switchMap(() => SystemFileAPI.restoreSystemFile(id)),
      this.itemRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('restore_system_file'),
      }),
    );
  };

  sortSystemFiles = (ids: SystemFileId[]) => {
    return SystemFileAPI.sortSystemFiles(ids).pipe(
      tap(() => this.fetchSystemFiles().subscribe()),
      handleMessage({
        type: requestMessage('sort_system_files'),
      }),
    );
  };
}
